import { Component, OnInit, OnDestroy } from '@angular/core';
import { CampusWard, ChurchUnitsDataService, LocalWard, SortService, ConfirmDialog, MessageDialog, SelectChurchUnitDialog, UnitType, ChurchUnit, CampusWardsDataService, NewLocalWard } from '../../../shared';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './local-ward-assignments.component.html',
  styleUrls: ['./local-ward-assignments.component.scss']
})
export class LocalWardAssignmentsComponent implements OnInit, OnDestroy {

  loading = false;
  loadingError = false;

  foundLocalWard: LocalWard = null; // references the specific local ward the user wants to work with
  assignedLocalWards: LocalWard[] = null; // references the local wards assigned to the campus ward the user wants to work with 
  selectedLocalWards: LocalWard[];

  assignableCampusWards: CampusWard[];
  selectedWardToAssign: CampusWard;

  reassigningWard = false;
  reassignedWard: CampusWard; // Lets us know that a reassignment happened so that we can show the "success" message

  removingWardAssignment = false;
  removedWardAssignments = false;

  addingAssignment = false;

  private campusWardId = NaN;
  private campusHomeWardId = NaN;
  private parameterSubscription: Subscription;

  constructor(private route: ActivatedRoute, private dialog: MatDialog, private churchService: ChurchUnitsDataService,
              private wardService: CampusWardsDataService, private sorter: SortService) {
    // subscribe to our route parameters' values for the local ward or singles ward
    this.parameterSubscription = this.route.queryParams.subscribe(params => {
      this.campusWardId = +params['campusWardId'];
      this.campusHomeWardId =  +params['campusHomeWardId'];
      this.loadInformation();
    });
  }

  ngOnInit() {
    // load the wards the user can pick from
    this.wardService.getCampusWards().subscribe(wards => {
      this.assignableCampusWards = wards.filter(ward => !ward.isForMarriedStudents);
    });
  }

  ngOnDestroy() {
    this.parameterSubscription.unsubscribe();
  }

  /**
   * Opens up the dialog to find a ward/branch and then add a local ward assignment for it.  Only available if the
   * person is looking at local wards assigned to a campus ward.
   */
  promptAddAssignment(): void {
    if (this.campusWardId && !this.addingAssignment) {
      const dialog = this.dialog.open(SelectChurchUnitDialog, {
        data: { validUnitTypes: [UnitType.Branch, UnitType.Ward] }
      });

      dialog.afterClosed().subscribe((selectedUnit: ChurchUnit) => {
        if (selectedUnit) {
            this.clearResultsOfReassignmentOrRemoval();
            this.addingAssignment = true;
            this.churchService.createLocalWardAssignment(this.campusWardId, selectedUnit.unitNumber)
              .pipe(
                finalize(() => this.addingAssignment = false)
              )
              .subscribe((ward) => {
                  this.assignedLocalWards.push(ward);
                  this.assignedLocalWards.sort(this.sorter.sortLocalWardsAsc);
                },
                error => {
                  this.showError(error);
                });
            }
      });
    }
  }

  /**
   * Reassigns the selected wards and then reloads based on our parameters.
   */
  reassignWard() {
    const localWards = this.getLocalWardsToActOn();
    if (localWards.length === 0 || this.reassigningWard || !this.selectedWardToAssign) {
      return;
    }

    const message = `Are you sure you want to reassign ${localWards.length === 1 ? 'this ward' : `these ${this.selectedLocalWards.length} wards`}?`;
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.reassigningWard = true;
        this.clearResultsOfReassignmentOrRemoval();

        // store this off before in case the user changes their selection while we're updating the addresses
        const wardToAssign = this.selectedWardToAssign;

        this.churchService.updateLocalWardAssignment(this.selectedWardToAssign.campusWardId, localWards.map(w => w.campusHomeWardId))
          .pipe(
            finalize(() => this.reassigningWard = false)
          )
          .subscribe(() => {
            this.reassignedWard = wardToAssign;
            this.reloadAfterUpdate();
          },
          error => {
            this.showError(error);
          });
      }
    });
  }

  /**
   * Removes the assigned ward from the selected local wards.
   */
  removeWardAssignment() {
    const localWards = this.getLocalWardsToActOn();
    if (localWards.length === 0 || this.removingWardAssignment) {
      return;
    }

    const message = `Are you sure you want to unassign ${localWards.length === 1 ? 'this ward' : `these ${this.selectedLocalWards.length} wards`}?`;
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.removingWardAssignment = true;
        this.clearResultsOfReassignmentOrRemoval();

        this.churchService.removeLocalWardAssignment(localWards.map(a => a.campusHomeWardId))
          .pipe(
            finalize(() => this.removingWardAssignment = false)
          )
          .subscribe(() => {
            this.removedWardAssignments = true;
            this.reloadAfterUpdate();
          },
          error => {
            this.showError(error);
          });
      }
    });
  }

  /**
   * Whether or not our data has loaded, either for looking at one particular local ward or looking at all of the local wards
   * assigned to a campus ward.
   */
  dataHasLoaded(): boolean {
    return this.foundLocalWard != null || this.assignedLocalWards != null;
  }

  /**
   * Lets us know if we're working with a specific campus ward and all wards assigned to it or not.  If not,
   * then we're working with a single local ward.  This changes what options the user has.
   */
  viewingLocalWardsAssignedToCampusWard(): boolean {
    return this.assignedLocalWards != null;
  }

  /**
   * Reset our initial state, then load our data.
   */
  private loadInformation() {
    this.foundLocalWard = null;
    this.assignedLocalWards = null;
    this.clearResultsOfReassignmentOrRemoval();
    this.reloadAfterUpdate();
  }

  /**
   * Based on our paramters, load the local ward or local wards assigned to the selected campus ward.
   */
  private reloadAfterUpdate() {

    if ((!isNaN(this.campusWardId) || !isNaN(this.campusHomeWardId))) {
      // if they give us both, only look take into account the single's ward
      if (this.campusWardId) {
        this.loadAssignedLocalWards();
      } else {
        this.loadLocalWard();
      }
    }
  }

  /**
   * Loads the local ward that the user wants to view/change the assignment of (based on query parameter we previously stored off).
   */
  private loadLocalWard() {
    if (!this.loading && !isNaN(this.campusHomeWardId)) {
      this.loading = true;
      this.churchService.getLocalWard(this.campusHomeWardId)
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe(localWard => {
        this.foundLocalWard = localWard;
      },
      error => {
        this.loadingError = true;
      });
    }
  }

  /**
   * Loads the local wards that have been assigned to the campus ward that the user wants to view/change (based on query parameter we previously stored off).
   */
  private loadAssignedLocalWards() {
    if (!this.loading && !isNaN(this.campusWardId)) {
      this.loading = true;
      this.wardService.getAssignedLocalWards(this.campusWardId)
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe(localWards => {
        this.assignedLocalWards = localWards;
        this.assignedLocalWards.sort(this.sorter.sortLocalWardsAsc);
      },
      error => {
        this.loadingError = true;
      });
    }
  }

  /**
   * Returns the local wards that the user wants to reassign or remove the ward assignment of.  This is based on whether we're viewing a single local ward
   * or multiple local wards assigned to a campus ward.
   */
  private getLocalWardsToActOn(): LocalWard[] {
    if (this.viewingLocalWardsAssignedToCampusWard()) {
      return this.selectedLocalWards;
    } else if (this.foundLocalWard) {
      return [this.foundLocalWard];
    }

    return [];
  }

  /**
   * Clears the properties that report success of a reassignment or removal of assignment.
   */
  private clearResultsOfReassignmentOrRemoval() {
    this.reassignedWard = null;
    this.removedWardAssignments = false;
  }

  /**
   * Opens a confirm dialog with the given message.
   */
  private openConfirmDialog(message: string): MatDialogRef<ConfirmDialog, any> {
    return this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: 'Please confirm', message: message}
    });
  }

  /**
   * Shows an error dialog.
   */
  private showError(message: string) {
    const errorDialog = this.dialog.open(MessageDialog, {
      data: { message: message, isError: true }
    });
  }
}
